import { Map } from 'immutable';
import { normalize } from 'normalizr';

import {
  getSchemaByEndpoint,
  isBrowseGetDirectoryEndpoint,
  isCatalogAnnotationEndpoint,
  isCatalogDetailsEndpoint,
} from '../../lib/ApiUtils';
import { mergeCatalogAnnotation, mergeCatalogDetails } from '../../reducers/entities/Catalog';
import Logger from '../../tools/Logger';
import { type ApiEndpoint, type ApiSchema, type State } from '../../types';
import { type ApiEndpointStore } from '../../types/globals/window';
import { ERROR_TYPE_API } from '../SentryClient';

export const log = Logger.child({
  component: 'lib/configuration/ApiDataMapper',
  errorType: ERROR_TYPE_API,
});

/**
 * Merges the newly normalized entities onto the state entities object.
 *
 * @param {object} state
 * @param {object} data - The api data to be normalized
 * @param {object} schema
 * @returns {object} mutated state
 */
function normalizeApiData(state: State, data: $TSFixMe | undefined, schema: ApiSchema): State {
  if (!data) {
    return state;
  }
  const normalizedApiData = normalize(data, schema);
  if (normalizedApiData && normalizedApiData.entities) {
    // @ts-expect-error
    return state.mergeDeep(normalizedApiData.entities);
  }
  return state;
}

/**
 * Returns a transform function to apply to an endpoint data
 *
 * @param {string} apiEndpoint
 * @returns {Function|null}
 */
function getTransformByEndpoint(
  apiEndpoint: ApiEndpoint,
): typeof mergeCatalogAnnotation | typeof mergeCatalogDetails | undefined {
  if (isCatalogAnnotationEndpoint(apiEndpoint)) {
    return mergeCatalogAnnotation;
  } else if (isCatalogDetailsEndpoint(apiEndpoint)) {
    return mergeCatalogDetails;
  }
  return undefined;
}

/**
 * Transforms API data, either by applying a schema, or applying a transform
 *
 * @param {object} state
 * @param {Array} apiData - nested api data resources
 * @param {object} schema
 * @param {Function} transformByEndpoint
 * @returns {object} mutated state
 */
function mapNestedApiDataToSubstate(
  state: $TSFixMe,
  apiData: $TSFixMe[] = [],
  schema: ApiSchema,
  transformByEndpoint: ReturnType<typeof getTransformByEndpoint>,
): $TSFixMe[] {
  const apiDataArray = apiData ? (Array.isArray(apiData) ? apiData : [apiData]) : [];
  return apiDataArray.reduce(
    (newState, data) =>
      transformByEndpoint?.(newState, data) ?? normalizeApiData(newState, data, schema),
    state,
  );
}

/**
 * Iterates over the raw top level api Data, mapping each nested api data resource.
 *
 * @param {object} apiData - the raw api data, as provided by the server
 * @param {object} initialState - the state to merge with apiData
 * @returns {object} mutated state
 */
export default function mapApiDataToSubstate(
  apiData: ApiEndpointStore = {} as ApiEndpointStore,
  initialState = Map<$TSFixMe>({}),
): $TSFixMe {
  return (Object.keys(apiData ?? {}) as ApiEndpoint[]).reduce((state, apiEndpoint): $TSFixMe => {
    if (isBrowseGetDirectoryEndpoint(apiEndpoint)) {
      // PLS browse data will be added to state if/when discovery domain is loaded
      return state;
    }
    let schema: ApiSchema;
    try {
      schema = getSchemaByEndpoint(apiEndpoint);
    } catch (e) {
      log.warn((e as object).toString(), 'Error encountered when getting schema by endpoint.');
      return state;
    }
    const transform = getTransformByEndpoint(apiEndpoint);
    return mapNestedApiDataToSubstate(state, apiData[apiEndpoint], schema, transform);
  }, initialState);
}
